import type { HouseholdType, Inputs, Projection } from '../calc/types';
import type {
  ClimateFactor,
  CostOfLivingFactor,
  CountryProfile,
  Factor,
  FactorKey,
  HealthcareFactor,
  Source,
} from '../countries/types';
import { COUNTRIES } from '../countries/data';
import {
  BAND_LABEL,
  BAND_ORDER,
  FACTOR_WEIGHTS,
  climateRating,
  healthcareRating,
  ratingOf,
  safetyRating,
  type Band,
  type ScoredFactor,
} from '../countries/score';
import { fetchRates, type FxRates, type RateOrigin } from '../countries/fx';
import { compareAll, COUPLE_COST_OF_LIVING_MULTIPLIER, type CountryComparison } from '../countries/compare';
import { formatMoney, formatMoneyCompact, splitEmphasis } from './format';

export interface CountryElements {
  picker: HTMLSelectElement;
  detail: HTMLElement;
  recommendations: HTMLElement;
  tiers: HTMLElement;
  methodology: HTMLElement;
  fullTable: HTMLElement;
}

/**
 * Static content, independent of the user's numbers, so it renders once at
 * startup rather than on every projection change. Shown once for the whole
 * page now, not duplicated inside every country's detail view.
 */
export function initMethodology(host: HTMLElement): void {
  host.replaceChildren(methodologyList());
}

/** Fills every `[data-country-count]` from the data, so the copy can't drift when countries are added. */
export function initCountryCount(root: ParentNode = document): void {
  for (const el of root.querySelectorAll('[data-country-count]')) el.textContent = String(COUNTRIES.length);
}

/** One-time DOM population, sorted alphabetically for easy scanning. */
export function initCountryPicker(select: HTMLSelectElement): void {
  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.textContent = 'Choose a country…';

  const sorted = [...COUNTRIES].sort((a, b) => a.name.localeCompare(b.name));
  select.replaceChildren(
    placeholder,
    ...sorted.map((country) => {
      const option = document.createElement('option');
      option.value = country.code;
      option.textContent = country.name;
      return option;
    }),
  );
}

/** Guards against a slow FX fetch resolving after a newer one was already requested. */
let renderToken = 0;

export async function renderCountries(
  elements: CountryElements,
  input: Inputs,
  projection: Projection | null,
): Promise<void> {
  const token = ++renderToken;

  if (!projection) {
    elements.tiers.replaceChildren(emptyNotice('Enter your numbers above to see how far your retirement income goes in each country.'));
    elements.recommendations.replaceChildren();
    elements.detail.replaceChildren();
    elements.fullTable.replaceChildren();
    return;
  }

  const rates = await fetchRates(input.currency);
  if (token !== renderToken) return; // a newer render has since started — drop this stale one

  const comparisons = compareAll(COUNTRIES, projection, rates, input.householdType);

  renderTiers(elements.tiers, comparisons);
  renderRecommendations(elements.recommendations, comparisons);
  renderFullTable(elements.fullTable, comparisons, rates);
  renderPickedDetail(elements, comparisons, rates, input.householdType);
}

function renderPickedDetail(
  elements: CountryElements,
  comparisons: CountryComparison[],
  rates: FxRates,
  householdType: HouseholdType,
): void {
  const picked = comparisons.find((c) => c.country.code === elements.picker.value);
  if (!picked) {
    elements.detail.replaceChildren();
    return;
  }
  elements.detail.replaceChildren(countryDetail(picked, rates, householdType));
}

/** Short column headers for the full table — there's no room there for the full factor names. */
const FULL_TABLE_FACTOR_LABELS: Record<ScoredFactor, string> = {
  expatTaxTreatment: 'Tax/Visa',
  healthcare: 'Health',
  propertyRights: 'Property',
  climate: 'Climate',
  safety: 'Safety',
  pensionUprating: 'Pension',
};

const FULL_TABLE_FACTORS = Object.keys(FACTOR_WEIGHTS) as ScoredFactor[];

/**
 * The transparency escape hatch: every country, every scored factor, in one
 * table, collapsed by default. The band tables above are the curated,
 * opinionated view; this is for anyone who wants to check the working or
 * second-guess a verdict rather than take the three bands on trust.
 */
function renderFullTable(host: HTMLElement, comparisons: CountryComparison[], rates: FxRates): void {
  const sorted = [...comparisons].sort((a, b) => b.score - a.score || b.surplusRealHome - a.surplusRealHome);

  const details = document.createElement('details');
  details.className = 'full-table-disclosure';
  const summary = document.createElement('summary');
  summary.textContent = 'See the full comparison table';
  details.append(summary);

  const scroll = document.createElement('div');
  scroll.className = 'full-table-wrap';

  const table = document.createElement('table');
  table.className = 'full-table';

  const head = document.createElement('thead');
  const headRow = document.createElement('tr');
  for (const label of ['Country', 'Score', ...FULL_TABLE_FACTORS.map((f) => FULL_TABLE_FACTOR_LABELS[f]), 'Cost ±']) {
    const th = document.createElement('th');
    th.setAttribute('scope', 'col');
    th.textContent = label;
    headRow.append(th);
  }
  head.append(headRow);
  table.append(head);

  const body = document.createElement('tbody');
  for (const item of sorted) {
    const tr = document.createElement('tr');

    const name = document.createElement('td');
    name.className = 'full-table__country';
    nameWithFlag(name, item.country);
    tr.append(name);

    const score = document.createElement('td');
    score.textContent = formatScore(item.score);
    tr.append(score);

    for (const factor of FULL_TABLE_FACTORS) {
      const rating = ratingOf(item.country, factor);
      const td = document.createElement('td');
      const badge = document.createElement('span');
      badge.className = `tier-badge tier-badge--${rating}`;
      badge.textContent = rating;
      td.append(badge);
      tr.append(td);
    }

    const positive = item.surplusRealHome >= 0;
    const cost = document.createElement('td');
    cost.className = `tier-row__surplus ${positive ? 'tier-row__surplus--positive' : 'tier-row__surplus--negative'}`;
    cost.textContent = `${positive ? '+' : '−'}${formatMoneyCompact(Math.abs(item.surplusRealHome), item.homeCurrency)}/yr`;
    tr.append(cost);

    body.append(tr);
  }
  table.append(body);
  scroll.append(table);
  details.append(scroll);
  host.replaceChildren(details, costOfLivingIndexTable(comparisons), exchangeRatesDisclosure(comparisons, rates));
}

/** The raw input behind `comfortableSingleLocal` in `compare.ts`, so anyone can check the ratio for themselves. */
const NUMBEO_COST_OF_LIVING_SOURCE: Source[] = [
  {
    label: 'Numbeo — Cost of Living Index by country',
    url: 'https://www.numbeo.com/cost-of-living/rankings_by_country.jsp',
    asOf: '2026-09-23',
    covers: ['costOfLiving'],
  },
];

function costOfLivingIndexTable(comparisons: CountryComparison[]): HTMLElement {
  const sorted = [...comparisons].sort((a, b) => a.country.name.localeCompare(b.country.name));

  const details = document.createElement('details');
  details.className = 'full-table-disclosure';
  const summary = document.createElement('summary');
  summary.textContent = 'Numbeo Cost of Living Index, by country';
  details.append(summary);

  const scroll = document.createElement('div');
  scroll.className = 'full-table-wrap';

  const table = document.createElement('table');
  table.className = 'full-table';

  const head = document.createElement('thead');
  const headRow = document.createElement('tr');
  for (const label of ['Country', 'Cost of Living Index']) {
    const th = document.createElement('th');
    th.setAttribute('scope', 'col');
    th.textContent = label;
    headRow.append(th);
  }
  head.append(headRow);
  table.append(head);

  const body = document.createElement('tbody');
  for (const item of sorted) {
    const tr = document.createElement('tr');
    const name = document.createElement('td');
    name.className = 'full-table__country';
    nameWithFlag(name, item.country);
    const index = document.createElement('td');
    index.textContent = item.country.factors.costOfLiving.costOfLivingIndex.toFixed(1);
    tr.append(name, index);
    body.append(tr);
  }
  table.append(body);
  scroll.append(table);
  details.append(scroll, citationLine(NUMBEO_COST_OF_LIVING_SOURCE));
  return details;
}

const RATE_SOURCE_LABEL: Record<RateOrigin['source'], string> = {
  ecb: 'European Central Bank',
  'exchangerate-api': 'ExchangeRate-API',
  peg: 'Fixed to the US dollar',
  snapshot: 'Built-in estimate',
};

function formatRate(rate: number): string {
  const digits = rate >= 100 ? 0 : rate >= 10 ? 2 : 3;
  return rate.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function link(href: string, text: string): HTMLAnchorElement {
  const a = document.createElement('a');
  a.href = href;
  a.textContent = text;
  a.rel = 'noopener';
  return a;
}

/**
 * The "how do the currency figures work" disclosure: what gets fetched, from
 * whom, what's sent, where the rates feed in, and the rate actually used for
 * each destination currency today with its source and date.
 */
function exchangeRatesDisclosure(comparisons: CountryComparison[], rates: FxRates): HTMLElement {
  const details = document.createElement('details');
  details.className = 'full-table-disclosure';
  const summary = document.createElement('summary');
  summary.textContent = 'Currency exchange rates';
  details.append(summary);

  const intro = document.createElement('p');
  intro.className = 'disclosure__text';
  intro.textContent =
    'When you explore destinations, your browser pings two free exchange-rate services for the latest ' +
    `rates against your pension currency (${rates.base}). The only thing sent is that currency code, never ` +
    'your personal or financial details.';

  const sources = document.createElement('ul');
  sources.className = 'disclosure__list';
  const items: (string | HTMLElement)[][] = [
    [
      ecbLink(),
      ' reference rates, via ',
      link('https://frankfurter.dev', 'Frankfurter'),
      ', for most currencies. Updated each working day.',
    ],
    [
      link('https://www.exchangerate-api.com', 'ExchangeRate-API'),
      ' for the few currencies the European Central Bank doesn\'t publish. Updated daily. ',
      exchangeRateApiCredit(),
    ],
    ['The Barbadian dollar is fixed at 2 to the US dollar, so it\'s worked out from the US dollar rate.'],
    [
      'If neither service can be reached, a built-in estimate is used instead, and each country\'s details say so, with its date.',
    ],
  ];
  for (const parts of items) {
    const li = document.createElement('li');
    li.append(...parts);
    sources.append(li);
  }

  const uses = document.createElement('p');
  uses.className = 'disclosure__text';
  uses.textContent =
    'The rates convert your projected income into each country\'s currency, and each country\'s living ' +
    'costs back into yours. That gap is the Cost ± figure in the tables, the "a year to spare" line on the ' +
    'top places, and the income, living cost and spare figures in each country\'s details. Exchange rates ' +
    'never change a country\'s score.';

  const currencies = [...new Set(comparisons.map((c) => c.country.currency))]
    .filter((code) => code !== rates.base)
    .sort((a, b) => currencyName(a).localeCompare(currencyName(b)));

  const scroll = document.createElement('div');
  scroll.className = 'full-table-wrap';
  const table = document.createElement('table');
  table.className = 'full-table';
  const head = document.createElement('thead');
  const headRow = document.createElement('tr');
  for (const label of ['Currency', `1 ${rates.base} =`, 'Source', 'Date']) {
    const th = document.createElement('th');
    th.setAttribute('scope', 'col');
    th.textContent = label;
    headRow.append(th);
  }
  head.append(headRow);
  table.append(head);
  const body = document.createElement('tbody');
  for (const code of currencies) {
    const origin = rates.origins[code];
    const rate = rates.rates[code];
    const tr = document.createElement('tr');
    const cells = [
      `${currencyName(code)} (${code})`,
      rate === undefined ? 'n/a' : `${formatRate(rate)} ${code}`,
      origin ? RATE_SOURCE_LABEL[origin.source] : 'n/a',
      origin ? formatRateDate(origin.asOf) : 'n/a',
    ];
    cells.forEach((text, i) => {
      const td = document.createElement('td');
      if (i === 0) td.className = 'full-table__country';
      td.textContent = text;
      tr.append(td);
    });
    body.append(tr);
  }
  table.append(body);
  scroll.append(table);

  details.append(intro, sources, uses, scroll);
  return details;
}

function renderTiers(host: HTMLElement, comparisons: CountryComparison[]): void {
  const groups: Record<Band, CountryComparison[]> = { pack: [], look: [], think: [] };
  for (const c of comparisons) groups[c.band].push(c);
  // Score orders the column; the surplus settles ties, in the home currency —
  // sorting the local figures would compare yen against euros and rank by
  // exchange rate rather than by value.
  for (const list of Object.values(groups)) {
    list.sort((a, b) => b.score - a.score || b.surplusRealHome - a.surplusRealHome);
  }

  host.replaceChildren(...BAND_ORDER.map((band) => tierGroup(band, groups[band])));
}

function tierGroup(band: Band, items: CountryComparison[]): HTMLElement {
  const section = document.createElement('div');
  section.className = `tier-group tier-group--${band}`;

  const h3 = document.createElement('h3');
  const badge = document.createElement('span');
  badge.className = `tier-badge tier-badge--${band}`;
  badge.textContent = BAND_LABEL[band];
  h3.append(badge);
  section.append(h3);

  /*
   * A real table rather than a list: the two figures need to line up under
   * their headings down the column, and "Affordability" is wider than any
   * value beneath it, so the column has to size itself to whichever is
   * bigger. A table does that on its own.
   */
  const table = document.createElement('table');
  table.className = 'tier-table';

  const head = document.createElement('thead');
  const headRow = document.createElement('tr');
  for (const [label, className] of [
    ['Country', 'tier-table__country'],
    ['Score', 'tier-table__score'],
    ['Cost ±', 'tier-table__money'],
  ] as const) {
    const th = document.createElement('th');
    th.setAttribute('scope', 'col');
    th.className = className;
    th.textContent = label;
    headRow.append(th);
  }
  head.append(headRow);
  table.append(head);

  const body = document.createElement('tbody');
  for (const item of items) body.append(tierRow(item));
  table.append(body);
  section.append(table);

  return section;
}

function tierRow(item: CountryComparison): HTMLElement {
  const tr = document.createElement('tr');
  tr.className = 'tier-row';

  const name = document.createElement('td');
  name.className = 'tier-table__country tier-row__name';
  nameWithFlag(name, item.country, { short: true });

  const score = document.createElement('td');
  score.className = 'tier-table__score tier-row__score';
  score.textContent = formatScore(item.score);

  const positive = item.surplusRealHome >= 0;
  const surplus = document.createElement('td');
  surplus.className = `tier-table__money tier-row__surplus ${positive ? 'tier-row__surplus--positive' : 'tier-row__surplus--negative'}`;
  surplus.textContent = `${positive ? '+' : '−'}${formatMoneyCompact(Math.abs(item.surplusRealHome), item.homeCurrency)}/yr`;

  tr.append(name, score, surplus);
  return tr;
}

/** Ratings are three-level, so a decimal would imply precision the score hasn't got. */
function formatScore(score: number): string {
  return String(Math.round(score));
}

/**
 * Shown when no country is covered yet. The shortfall against each one is
 * already in the tables below, so repeating the near misses here would say
 * the same thing twice — better to point at the lever that changes it.
 */
function keepSavingNotice(): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'recommendations';

  const h3 = document.createElement('h3');
  h3.textContent = 'There’s still time';
  wrap.append(h3);

  const body = document.createElement('p');
  body.className = 'recommendations__intro';
  body.textContent =
    'None of these destinations is covered by your projected income yet. Adjust your contributions above to see how much more you would need to save.';
  wrap.append(body);

  return wrap;
}


/**
 * Where the score and the money meet. Affordability gates — there is no point
 * recommending somewhere your pension cannot cover — and the score then ranks
 * what is left, so the best place you can actually afford comes first. Every
 * band is eligible: a cheap country that scores modestly is still somewhere
 * you could go, which is more use than a perfect one you cannot.
 */
/** Cards come in rows of three; past three rows they'd just repeat the band tables below. */
const RECOMMENDATION_ROW = 3;
const RECOMMENDATION_MAX = RECOMMENDATION_ROW * 3;

/**
 * How many cards the viewer has opened up. Kept across re-renders, so
 * nudging a contribution doesn't snap the list back to three.
 */
let recommendationsShown = RECOMMENDATION_ROW;

function renderRecommendations(host: HTMLElement, comparisons: CountryComparison[], focusFrom?: number): void {
  const affordable = comparisons.filter((c) => c.surplusRealHome > 0);

  if (affordable.length === 0) {
    host.replaceChildren(keepSavingNotice());
    return;
  }

  const top = [...affordable]
    .sort((a, b) => b.score - a.score || b.surplusRealHome - a.surplusRealHome)
    .slice(0, RECOMMENDATION_MAX);
  const shown = top.slice(0, recommendationsShown);

  const wrap = document.createElement('div');
  wrap.className = 'recommendations';

  const h3 = document.createElement('h3');
  h3.className = 'recommendations__title';
  h3.textContent = top.length === 1 ? 'Your top place to retire' : 'Your top places to retire';
  wrap.append(h3);

  const intro = document.createElement('p');
  intro.className = 'recommendations__intro';
  intro.textContent = 'The best-scoring places your projected income already covers.';
  wrap.append(intro);

  const list = document.createElement('ol');
  list.className = 'recommendations__list';
  for (const item of shown) list.append(recommendationCard(item));
  wrap.append(list);

  const remaining = top.length - shown.length;
  const expanded = shown.length > RECOMMENDATION_ROW;
  if (remaining > 0 || expanded) {
    const actions = document.createElement('div');
    actions.className = 'recommendations__actions';

    if (remaining > 0) {
      const more = document.createElement('button');
      more.type = 'button';
      more.className = 'recommendations__more';
      more.textContent = 'Show more';
      more.addEventListener('click', () => {
        const from = recommendationsShown;
        recommendationsShown += RECOMMENDATION_ROW;
        renderRecommendations(host, comparisons, from);
      });
      actions.append(more);
    }

    if (expanded) {
      const fewer = document.createElement('button');
      fewer.type = 'button';
      fewer.className = 'recommendations__more';
      fewer.textContent = 'Show fewer';
      fewer.addEventListener('click', () => {
        recommendationsShown = RECOMMENDATION_ROW;
        renderRecommendations(host, comparisons);
        // Folding away rows can leave the viewer staring at the band tables, so bring the cards back.
        host.querySelector('.recommendations__title')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        host.querySelector<HTMLElement>('.recommendations__more')?.focus({ preventScroll: true });
      });
      actions.append(fewer);
    }

    wrap.append(actions);
  }

  host.replaceChildren(wrap);

  // The button that was clicked is gone, so hand keyboard focus to the first new card.
  if (focusFrom !== undefined) {
    list.children[focusFrom]?.querySelector<HTMLElement>('.recommendation-card__more')?.focus();
  }
}

/** Country copy as nodes, with `*foreign words*` set in italics. Never parsed as HTML. */
function richText(text: string): (string | HTMLElement)[] {
  return splitEmphasis(text).map((run) => {
    if (!run.emphasis) return run.text;
    const em = document.createElement('em');
    em.textContent = run.text;
    return em;
  });
}

/** ISO 3166-1 alpha-2 to its flag emoji, via the regional-indicator letters. */
function flagOf(code: string): string {
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/** Fills `el` with the country's flag and name. The flag is decoration: screen readers get the name alone. */
function nameWithFlag(el: HTMLElement, country: CountryProfile, { short = false } = {}): void {
  const flag = document.createElement('span');
  flag.className = 'country-flag';
  flag.setAttribute('aria-hidden', 'true');
  flag.textContent = flagOf(country.code);
  if (short && country.shortName) {
    const abbr = document.createElement('abbr');
    abbr.title = country.name;
    abbr.textContent = country.shortName;
    el.replaceChildren(flag, abbr);
    return;
  }
  el.replaceChildren(flag, country.name);
}

function recommendationCard(item: CountryComparison): HTMLElement {
  const li = document.createElement('li');
  li.className = 'recommendation-card';

  const name = document.createElement('h4');
  name.className = 'recommendation-card__name';
  nameWithFlag(name, item.country);

  const spare = document.createElement('p');
  spare.className = 'recommendation-card__spare';
  const amount = document.createElement('strong');
  amount.textContent = `${formatMoney(item.surplusRealHome, item.homeCurrency)} a year to spare`;
  spare.append('Your income leaves you with', amount);

  const badge = document.createElement('span');
  badge.className = `tier-badge tier-badge--${item.band}`;
  badge.textContent = `${BAND_LABEL[item.band]} · ${formatScore(item.score)}`;

  const pitch = document.createElement('p');
  pitch.className = 'recommendation-card__pitch';
  pitch.append(...richText(item.country.pitch));

  const more = document.createElement('button');
  more.type = 'button';
  more.className = 'recommendation-card__more';
  more.textContent = 'See the details';
  more.addEventListener('click', () => showDetailFor(item.country.code));

  li.append(name, spare, badge, pitch, more);
  return li;
}

/** Drives the existing picker, so the detail view has a single way in. */
function showDetailFor(code: string): void {
  const picker = document.querySelector<HTMLSelectElement>('#country-picker');
  if (!picker) return;
  picker.value = code;
  picker.dispatchEvent(new Event('change'));
  // The detail renders asynchronously (it awaits FX rates), and replacing it
  // mid-scroll cancels a smooth scroll — so wait for it to land first.
  setTimeout(() => picker.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
}

function countryDetail(item: CountryComparison, rates: FxRates, householdType: HouseholdType): HTMLElement {
  const { country } = item;
  const wrap = document.createElement('div');
  wrap.className = 'country-detail';

  const header = document.createElement('div');
  header.className = 'country-detail__header';
  const h3 = document.createElement('h3');
  nameWithFlag(h3, country);
  const badge = document.createElement('span');
  badge.className = `tier-badge tier-badge--${item.band}`;
  badge.textContent = `${BAND_LABEL[item.band]} · ${formatScore(item.score)}`;
  header.append(h3, badge);
  wrap.append(header);

  const narrative = document.createElement('p');
  narrative.append(...richText(country.narrative));
  wrap.append(narrative);

  wrap.append(incomeComparison(item, rates, householdType));
  wrap.append(factorList(item));
  wrap.append(sourcesList(country));

  return wrap;
}

function incomeComparison(item: CountryComparison, rates: FxRates, householdType: HouseholdType): HTMLElement {
  const list = document.createElement('dl');
  list.className = 'stats country-detail__stats';

  const costOfLivingHint =
    householdType === 'couple'
      ? `a comfortable couple, approximate (${COUPLE_COST_OF_LIVING_MULTIPLIER}× a single retiree's budget)`
      : 'a comfortable single retiree, approximate';

  // Signed and coloured to match the Cost ± column in the band tables, so the
  // same fact does not change its appearance between the two places it appears.
  const covered = item.surplusReal >= 0;
  const gapInYourMoney = formatMoney(Math.abs(item.surplusRealHome), item.homeCurrency);

  const cells: { label: string; value: string; hint: string; valueClass?: string }[] = [
    {
      label: "Your income, today's money",
      value: formatMoney(item.incomeRealHome, item.homeCurrency),
      hint: `= ${formatMoney(item.incomeRealLocal, item.country.currency)} in ${item.country.name}`,
    },
    {
      label: 'Local cost of living',
      value: formatMoney(item.costOfLivingLocal, item.country.currency),
      hint: costOfLivingHint,
    },
    {
      label: covered ? 'Spare, per year' : 'Shortfall, per year',
      value: `${covered ? '+' : '−'}${formatMoney(Math.abs(item.surplusReal), item.country.currency)}`,
      // The figure above is in local money; the hint repeats it in the
      // currency your pension is actually paid in, which is the one you can
      // hold against the projection at the top of the page.
      hint: covered
        ? `covers the local budget, ${gapInYourMoney} to spare`
        : `income falls short by ${gapInYourMoney}`,
      valueClass: covered ? 'stats__value--positive' : 'stats__value--negative',
    },
  ];

  for (const { label, value, hint, valueClass } of cells) {
    const cell = document.createElement('div');
    cell.className = 'stats__cell';
    const dt = document.createElement('dt');
    dt.textContent = label;
    const dd = document.createElement('dd');
    dd.textContent = value;
    if (valueClass) dd.className = valueClass;
    const small = document.createElement('p');
    small.className = 'stats__hint';
    small.textContent = hint;
    cell.append(dt, dd, small);
    list.append(cell);
  }

  const note = document.createElement('p');
  note.className = 'chart__stage-note';
  note.append(...rateNote(item.country.currency, rates));
  const wrap = document.createElement('div');
  wrap.append(list, note);
  return wrap;
}

const CURRENCY_NAMES = new Intl.DisplayNames(['en-GB'], { type: 'currency' });

function currencyName(code: string): string {
  return CURRENCY_NAMES.of(code) ?? code;
}

function formatRateDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** ExchangeRate-API's terms ask for this exact link wherever its rates are shown. */
function exchangeRateApiCredit(): HTMLAnchorElement {
  const a = document.createElement('a');
  a.href = 'https://www.exchangerate-api.com';
  a.textContent = 'Rates By Exchange Rate API';
  a.rel = 'noopener';
  return a;
}

/** The ECB's own page for the reference rates Frankfurter republishes. */
function ecbLink(): HTMLAnchorElement {
  const a = document.createElement('a');
  a.href = 'https://www.ecb.europa.eu/stats/policy_and_exchange_rates/euro_reference_exchange_rates/html/index.en.html';
  a.textContent = 'European Central Bank';
  a.rel = 'noopener';
  return a;
}

/** Says plainly where this country's rate came from and how old it is. */
function rateNote(currency: string, rates: FxRates): (string | HTMLElement)[] {
  if (currency === rates.base) return ['No currency conversion needed: this country uses your currency.'];
  const origin = rates.origins[currency];
  const name = currencyName(currency);
  if (!origin) return [`No exchange rate available for the ${name}.`];
  const date = formatRateDate(origin.asOf);
  switch (origin.source) {
    case 'ecb':
      return ['Exchange rate: the ', ecbLink(), `'s reference rate from ${date}, updated each working day.`];
    case 'exchangerate-api':
      return [
        `Exchange rate: ExchangeRate-API's rate from ${date}, updated daily. `,
        exchangeRateApiCredit(),
      ];
    case 'snapshot':
      return [`Live exchange rates couldn't be loaded, so the ${name} uses an estimate from ${date}.`];
    case 'peg': {
      const anchor = origin.anchor;
      if (!anchor) return [`Exchange rate for the ${name} from ${date}.`];
      const anchorName = currencyName(anchor.code);
      const fixed = `The ${name} is fixed at ${anchor.perAnchor} to the ${anchorName}`;
      if (!anchor.origin) return [`${fixed}, so this rate is exact.`];
      const anchorDate = formatRateDate(anchor.origin.asOf);
      if (anchor.origin.source === 'ecb') {
        return [`${fixed}, so it follows the `, ecbLink(), `'s ${anchorName} rate from ${anchorDate}.`];
      }
      if (anchor.origin.source === 'exchangerate-api') {
        return [`${fixed}, so it follows ExchangeRate-API's ${anchorName} rate from ${anchorDate}. `, exchangeRateApiCredit()];
      }
      return [`${fixed}. Live rates couldn't be loaded, so this uses an estimated ${anchorName} rate from ${anchorDate}.`];
    }
  }
}

const FACTOR_LABELS = {
  costOfLiving: 'Cost of living',
  healthcare: 'Healthcare',
  climate: 'Climate',
  safety: 'Safety',
  propertyRights: 'Can you buy property?',
  expatTaxTreatment: 'Tax or Visa treatment for retirees',
  pensionUprating: 'State Pension uprating',
} as const;

function sourcesFor(country: CountryProfile, key: FactorKey): Source[] {
  return country.sources.filter((s) => s.covers?.includes(key));
}

function factorList(item: CountryComparison): HTMLElement {
  const country = item.country;
  const list = document.createElement('ul');
  list.className = 'factor-list';
  for (const key of Object.keys(FACTOR_LABELS) as (keyof typeof FACTOR_LABELS)[]) {
    const sources = sourcesFor(country, key);
    if (key === 'healthcare') {
      const h = country.factors.healthcare;
      list.append(
        factorRow(FACTOR_LABELS[key], { rating: healthcareRating(h), note: h.note }, sources, healthcareDetail(h)),
      );
      continue;
    }
    if (key === 'costOfLiving') {
      list.append(
        costOfLivingRow(country.factors.costOfLiving, item.costOfLivingSingleLocal, country.currency, sources),
      );
      list.append(everydayLifeRow(country.everydayLife));
      continue;
    }
    if (key === 'safety') {
      const sf = country.factors.safety;
      list.append(factorRow(FACTOR_LABELS[key], { rating: safetyRating(sf), note: sf.note }, sources));
      continue;
    }
    if (key === 'climate') {
      const c = country.factors.climate;
      list.append(
        factorRow(FACTOR_LABELS[key], { rating: climateRating(c), note: c.note }, sources, climateDetail(c), c.tag),
      );
      continue;
    }
    list.append(factorRow(FACTOR_LABELS[key], country.factors[key], sources));
  }
  list.append(doubleTaxTreatyRow(country.hasDoubleTaxTreaty, sourcesFor(country, 'doubleTaxTreaty')));
  return list;
}

/**
 * Just the EU-passport distinction, which the main note doesn't otherwise
 * state. Quality, access and private cost feed `healthcareRating` behind the
 * scenes but aren't restated here — the note above already says them in
 * plain English.
 */
function healthcareDetail(h: HealthcareFactor): string[] {
  return h.euCitizenNote ? [`If you hold an EU passport: ${h.euCitizenNote}`] : [];
}

function formatTempRange(range: { low: number; high: number }): string {
  return `Typical range: ${range.low}°C – ${range.high}°C`;
}

/**
 * Just the temperature range. Pollution, climate risk and policy performance
 * — the three inputs behind the rating — are already said in plain English
 * in the note; restating them as raw ranks here would be the same
 * redundancy just removed from the healthcare and cost-of-living rows.
 */
function climateDetail(c: ClimateFactor): string[] {
  return [formatTempRange(c.avgTempRangeC)];
}

function citationLine(sources: Source[]): HTMLElement {
  const p = document.createElement('p');
  p.className = 'factor-row__source';
  if (sources.length === 0) {
    p.textContent = 'No specific source cited — general estimate.';
    return p;
  }
  p.append('Source: ');
  sources.forEach((source, i) => {
    if (i > 0) p.append(', ');
    const link = document.createElement('a');
    link.href = source.url;
    link.textContent = source.label;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    p.append(link);
  });
  return p;
}

function factorRow(label: string, factor: Factor, sources: Source[], detail: string[] = [], tag?: string): HTMLElement {
  const li = document.createElement('li');
  li.className = 'factor-row';

  const head = document.createElement('div');
  head.className = 'factor-row__head';
  const name = document.createElement('span');
  name.className = 'factor-row__label';
  name.textContent = label;

  const right = document.createElement('span');
  right.className = 'factor-row__right';
  if (tag) {
    const tagEl = document.createElement('span');
    tagEl.className = 'factor-row__value';
    tagEl.textContent = tag;
    right.append(tagEl);
  }
  const badge = document.createElement('span');
  badge.className = `tier-badge tier-badge--${factor.rating}`;
  badge.textContent = factor.rating;
  right.append(badge);

  head.append(name, right);

  const note = document.createElement('p');
  note.className = 'factor-row__note';
  note.append(...richText(factor.note));

  li.append(head, note);

  for (const line of detail) {
    const detailEl = document.createElement('p');
    detailEl.className = 'factor-row__note';
    detailEl.append(...richText(line));
    li.append(detailEl);
  }

  li.append(citationLine(sources));

  return li;
}

/**
 * No badge: cost of living isn't rated, favourable or otherwise — see the
 * comment on `CostOfLivingFactor`. This shows the actual figure instead, and
 * points back up to the surplus, which is where the real answer lives.
 */
function costOfLivingRow(
  factor: CostOfLivingFactor,
  singleAnnualLocal: number,
  currency: string,
  sources: Source[],
): HTMLElement {
  const li = document.createElement('li');
  li.className = 'factor-row';

  const head = document.createElement('div');
  head.className = 'factor-row__head';
  const name = document.createElement('span');
  name.className = 'factor-row__label';
  name.textContent = 'Cost of living';
  const value = document.createElement('span');
  value.className = 'factor-row__value';
  value.textContent = `${formatMoney(singleAnnualLocal, currency)}/yr`;
  head.append(name, value);

  const note = document.createElement('p');
  note.className = 'factor-row__note';
  note.append(...richText(factor.note));

  li.append(head, note, citationLine(sources));

  return li;
}

/** Culture and daily life, sitting with cost of living as the other "what's it like" row. Never scored. */
function everydayLifeRow(text: string): HTMLElement {
  const li = document.createElement('li');
  li.className = 'factor-row';

  const head = document.createElement('div');
  head.className = 'factor-row__head';
  const name = document.createElement('span');
  name.className = 'factor-row__label';
  name.textContent = 'Everyday life';
  const badge = document.createElement('span');
  badge.className = 'tier-badge tier-badge--favorable';
  badge.textContent = 'Culture';
  head.append(name, badge);

  const note = document.createElement('p');
  note.className = 'factor-row__note';
  note.append(...richText(text));

  li.append(head, note);
  return li;
}

function doubleTaxTreatyRow(hasTreaty: boolean, sources: Source[]): HTMLElement {
  const li = document.createElement('li');
  li.className = 'factor-row';

  const head = document.createElement('div');
  head.className = 'factor-row__head';
  const name = document.createElement('span');
  name.className = 'factor-row__label';
  name.textContent = 'Double taxation treaty with the UK';
  const badge = document.createElement('span');
  badge.className = `tier-badge tier-badge--${hasTreaty ? 'favorable' : 'unfavorable'}`;
  badge.textContent = hasTreaty ? 'Yes' : 'No';
  head.append(name, badge);

  li.append(head, citationLine(sources));
  return li;
}

const FACTOR_KEY_LABELS: Record<FactorKey, string> = {
  ...FACTOR_LABELS,
  doubleTaxTreaty: 'Double taxation treaty',
};

function sourcesList(country: CountryProfile): HTMLElement {
  const details = document.createElement('details');
  details.className = 'sources';
  const summary = document.createElement('summary');
  summary.textContent = 'Sources';
  details.append(summary);

  const list = document.createElement('ul');
  for (const source of country.sources) {
    const li = document.createElement('li');
    const link = document.createElement('a');
    link.href = source.url;
    link.textContent = source.label;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    const asOf = document.createElement('span');
    asOf.className = 'sources__asof';
    const covers = source.covers?.map((key) => FACTOR_KEY_LABELS[key]).join(', ');
    asOf.textContent = covers ? ` — covers ${covers} — reviewed ${source.asOf}` : ` — reviewed ${source.asOf}`;
    li.append(link, asOf);
    list.append(li);
  }
  details.append(list);
  return details;
}

/**
 * How each factor is actually decided. One entry per factor as they get
 * reworked; the rest still rest on a single editorial judgement and will be
 * written up here as that changes.
 */
const METHODOLOGY: readonly { title: string; body: string }[] = [
  {
    title: 'Cost of living',
    body:
      'Not scored. A cheap country can still be out of reach on a small pension, and a dear one can ' +
      'work on a large one; it comes down to your own numbers. Each budget is derived, not guessed: we ' +
      'take the PLSA Retirement Living Standards\' "comfortable" figure for a single UK retiree ' +
      '(£37,300/yr, via Hargreaves Lansdown), rescale it by the ratio of that country\'s Numbeo Cost of ' +
      'Living Index to the UK\'s, and convert it into local currency at the latest exchange rate. We ' +
      'then compare your projected income against it and show the surplus or shortfall in your own ' +
      'currency. It doesn\'t include rent or a mortgage.',
  },
  {
    title: 'Healthcare',
    body:
      '25% of the score. We look at quality (Numbeo\'s Health Care Index), how open the public system ' +
      'is to an arriving retiree, and what private cover costs, and combine them into a favourable, ' +
      'neutral or unfavourable rating. Ratings are written for a British passport holder; where an EU ' +
      'one changes the answer, the country says so.',
  },
  {
    title: 'Climate',
    body:
      '15% of the score. There\'s no universal "good" climate, so the short description (e.g. "Hot & ' +
      'humid") is just that, not a rating. What\'s scored is how stable and healthy the country looks ' +
      'for the next few decades: local pollution (Numbeo\'s Pollution Index), past exposure to extreme ' +
      'weather (Germanwatch\'s Climate Risk Index) and how seriously it\'s tackling climate change (the ' +
      'Climate Change Performance Index).',
  },
  {
    title: 'Safety',
    body:
      '15% of the score. We combine three indices: everyday crime (Numbeo\'s Crime Index), organised ' +
      'crime (the Global Organized Crime Index) and corruption (Transparency International\'s Corruption ' +
      'Perceptions Index). Each is split into thirds of its scale, and together they give a favourable, ' +
      'neutral or unfavourable rating.',
  },
  {
    title: 'Can you buy property?',
    body:
      '15% of the score. A factual question, so it\'s a straight rating rather than one built from ' +
      'separate inputs: open and unrestricted is favourable, allowed with a real hurdle (a permit, a ' +
      'reciprocity check, a zone you can\'t buy in) is neutral, and largely closed to non-residents is ' +
      'unfavourable.',
  },
  {
    title: 'Tax and visa',
    body:
      '25% of the score, joint largest with healthcare. Can a retiree actually move there, and what happens ' +
      'to their pension income when they do? Favourable means foreign pension income is taxed at zero ' +
      'or a flat rate below the UK\'s 20% basic rate, or there\'s a visa built for retirees. Neutral ' +
      'means you can get residency, ' +
      'but your pension is taxed under the ordinary rules. Unfavourable means there\'s no realistic ' +
      'residency route for a retiree, however low the tax.',
  },
  {
    title: 'State Pension uprating',
    body:
      '5% of the score. The UK State Pension itself, not the pot modelled above. It keeps rising each ' +
      'year only in the UK, the EEA, Switzerland and countries with a reciprocal agreement; everywhere ' +
      'else it\'s frozen. Frozen means it stops getting yearly increases while you live there, and it goes ' +
      'back up to the current rate if you move back to the UK.',
  },
  {
    title: 'Double taxation treaty',
    body: 'Not scored. A treaty with the UK stops your pension being taxed twice, but doesn\'t lower the rate.',
  },
];

function methodologyList(): HTMLElement {
  const details = document.createElement('details');
  details.className = 'sources';
  const summary = document.createElement('summary');
  summary.textContent = 'Methodology';
  details.append(summary);

  const list = document.createElement('ul');
  for (const entry of METHODOLOGY) {
    const li = document.createElement('li');
    const title = document.createElement('strong');
    // A title that is already a question needs no colon after it.
    title.textContent = `${entry.title}${entry.title.endsWith('?') ? '' : ':'} `;
    li.append(title, entry.body);
    list.append(li);
  }
  details.append(list);
  return details;
}

function emptyNotice(message: string): HTMLElement {
  const p = document.createElement('p');
  p.className = 'results__empty';
  p.textContent = message;
  return p;
}
